import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';

import Button from '../../../../components/shared/Button/Button';
import CurrentStepIndicator from '../../../../components/shared/CurrentStepIndicator/CurrentStepIndicator';
import Modal from '../../../../components/shared/Modal/Modal';
import {
  CandidateQuestionFormSchema,
  INITIAL_VALUES,
} from '../../../../constants/formConstants/strengthenQuestions/Candidate/CandidateQuestionForm';
import { formatCandidateQuestions, formatSubmitedVales } from '../../../../helpers/candidateQuestionHelper';
import { validateWebsiteURL } from '../../../../helpers/commonHelper';
import { IsMobile } from '../../../../helpers/IsMobile';

import CandidateDescription from './CandidateDescription/CandidateDescription';
import CandidateProgressBar from './CandidateProgressBar/CandidateProgressBar';
import CandidateStrengthenStep1 from './CandidateStrengthenStep1/CandidateStrengthenStep1';
import CandidateStrengthenStep2 from './CandidateStrengthenStep2/CandidateStrengthenStep2';

import styles from './CandidateQuestionsModal.module.scss';

const CandidateQuestionsModal = ({ data = {}, showModal = false, handleSubmit, handleOnSkip }) => {
  const { t } = useTranslation('common');
  const [currentStep, setcurrentStep] = useState(1);
  const [formValues, setFormValues] = useState(INITIAL_VALUES);
  const modalFullScreen = IsMobile();

  const { email, logo_image_thumb, name, organization_name, refered_by, job_title } = data;

  useEffect(() => {
    setcurrentStep(1);
    setFormValues(formatCandidateQuestions(INITIAL_VALUES, data));
  }, [data]);

  const onSubmit = useCallback(
    (submitValues) => {
      if (currentStep === 1) {
        setcurrentStep(2);
      } else {
        const valuesToSubmit = { ...submitValues, attention_required: true };
        handleSubmit(formatSubmitedVales(valuesToSubmit));
      }
    },
    [currentStep, handleSubmit]
  );

  const handleSkip = useCallback(
    (setValues) => {
      if (currentStep === 1) {
        setValues((prevValues) => ({
          ...prevValues,
          files: data.resume_file_links,
          links_json: data.links_json,
        }));
        setcurrentStep(2);
      } else {
        setcurrentStep(1);
        handleOnSkip();
      }
    },
    [currentStep, data]
  );

  const handleChangeStep = useCallback((step) => {
    setcurrentStep(step);
  }, []);

  const areUrlsInvalid = useCallback((links) => {
    const linksValidationArr = links.map((link) => (link.value ? validateWebsiteURL(link.value) : true));
    return linksValidationArr.includes(false);
  }, []);
  return (
    <div>
      <Modal
        show={showModal}
        onHide={() => {}}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        fullscreen={modalFullScreen}
      >
        <Modal.CustomHeader closeButton={false}>
          <Modal.Title id="contained-modal-title-vcenter">{t('candidateQuestions.applicationDetails')}</Modal.Title>
        </Modal.CustomHeader>
        <Modal.Body>
          <div>
            <CandidateDescription
              jobName={job_title}
              organizationName={organization_name}
              logoUrl={logo_image_thumb}
              candidateName={name}
              candidateEmail={email}
              referedBy={refered_by}
            />
          </div>
          <div>
            <Formik
              initialValues={formValues}
              onSubmit={onSubmit}
              enableReinitialize
              validationSchema={CandidateQuestionFormSchema}
            >
              {({ values, dirty, setValues }) => (
                <Form>
                  <CandidateProgressBar organizationName={organization_name} />

                  <CurrentStepIndicator
                    numberOfSteps={2}
                    currentStep={currentStep}
                    handleChangeStep={handleChangeStep}
                  />

                  {currentStep === 1 ? (
                    <CandidateStrengthenStep1 />
                  ) : (
                    <CandidateStrengthenStep2 organizationName={organization_name} disableFields />
                  )}

                  <div className={styles.buttonContainer}>
                    <Button variant="outline-primary" className={styles.button} onClick={() => handleSkip(setValues)}>
                      {t('skip')}
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      className={styles.button}
                      disabled={
                        (!dirty && currentStep === 2) ||
                        areUrlsInvalid(values.links_json) ||
                        (currentStep === 2 && organization_name === 'Connect Pediatrics' && !values.license_available) // New condition
                      }
                    >
                      {currentStep === 1 ? t('next') : t('save')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CandidateQuestionsModal;
